"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MeetingsContext = exports.CommandScope = exports.RetryHandler = void 0;
exports.getCustomizedKeys = getCustomizedKeys;
exports.renderTemplate = renderTemplate;
exports.isPersonalApp = isPersonalApp;
exports.isGroupApp = isGroupApp;
exports.needTabAndBotCode = needTabAndBotCode;
exports.needTabCode = needTabCode;
exports.isBot = isBot;
exports.containsBotBasedMessageExtension = containsBotBasedMessageExtension;
exports.isBotBasedMessageExtension = isBotBasedMessageExtension;
exports.isBotAndBotBasedMessageExtension = isBotAndBotBasedMessageExtension;
exports.needBotCode = needBotCode;
exports.containsUnsupportedFeature = containsUnsupportedFeature;
exports.getFeaturesFromAppDefinition = getFeaturesFromAppDefinition;
exports.hasMeetingExtension = hasMeetingExtension;
exports.normalizePath = normalizePath;
exports.getResolvedManifest = getResolvedManifest;
const tslib_1 = require("tslib");
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
const lodash_1 = require("lodash");
const mustache_1 = tslib_1.__importDefault(require("mustache"));
const common_1 = require("../../../utils/common");
const wrapUtil_1 = require("../../util/wrapUtil");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const error_1 = require("../../../../error");
const telemetry_1 = require("./telemetry");
const envFunctionUtils_1 = require("../../../utils/envFunctionUtils");
var retryHandler_1 = require("../../../../common/retryHandler");
Object.defineProperty(exports, "RetryHandler", { enumerable: true, get: function () { return retryHandler_1.RetryHandler; } });
function getCustomizedKeys(prefix, manifest) {
    let keys = [];
    for (const key in manifest) {
        if (manifest.hasOwnProperty(key)) {
            const value = manifest[key];
            if (typeof value === "object") {
                if (Array.isArray(value)) {
                    value.map((item, index) => {
                        keys = keys.concat(getCustomizedKeys(`${prefix}.${key}[${index}]`, item));
                    });
                }
                else {
                    keys = keys.concat(getCustomizedKeys(`${prefix}.${key}`, value));
                }
            }
            else if (typeof value === "string" && value.startsWith("{{config.manifest")) {
                keys.push(`${prefix}.${key}`);
            }
        }
    }
    return keys;
}
function renderTemplate(manifestString, view) {
    // Unesacped HTML
    mustache_1.default.escape = (value) => value;
    manifestString = mustache_1.default.render(manifestString, view);
    return manifestString;
}
function isPersonalApp(appDefinition) {
    const restrictedEntityIds = [
        "conversations",
        "recent",
        "about",
        "alltabs",
        "chat",
    ];
    if (!!appDefinition.staticTabs && appDefinition.staticTabs.length > 0) {
        return (appDefinition.staticTabs.filter((tab) => !restrictedEntityIds.includes(tab.entityId)).length >
            0);
    }
    return false;
}
function isGroupApp(appDefinition) {
    return (!!appDefinition.configurableTabs &&
        appDefinition.configurableTabs.length > 0 &&
        groupAppConfigured(appDefinition.configurableTabs[0]));
}
function needTabAndBotCode(appDefinition) {
    return needTabCode(appDefinition) && needBotCode(appDefinition);
}
function needTabCode(appDefinition) {
    return (isPersonalApp(appDefinition) || isGroupApp(appDefinition)) && !needBotCode(appDefinition);
}
function isBot(appDefinition) {
    return !!appDefinition.bots && appDefinition.bots.length > 0;
}
function containsBotBasedMessageExtension(appDefinition) {
    return (!!appDefinition.messagingExtensions &&
        appDefinition.messagingExtensions.length > 0 &&
        !!appDefinition.messagingExtensions[0].botId);
}
function isBotBasedMessageExtension(appDefinition) {
    return containsBotBasedMessageExtension(appDefinition) && !isBot(appDefinition);
}
function isBotAndBotBasedMessageExtension(appDefinition) {
    return isBot(appDefinition) && isBotBasedMessageExtension(appDefinition);
}
function needBotCode(appDefinition) {
    return isBot(appDefinition) || isBotBasedMessageExtension(appDefinition);
}
function isApiBasedMessageExtension(appDefinition) {
    return (!!appDefinition.messagingExtensions &&
        appDefinition.messagingExtensions.length > 0 &&
        appDefinition.messagingExtensions[0].messagingExtensionServiceType?.toLowerCase() === "apibased");
}
function containsUnsupportedFeature(appDefinition) {
    const hasScene = appDefinition?.meetingExtensionDefinition?.scenes?.length;
    const hasConnector = !!appDefinition?.connectors?.length;
    const hasActivies = appDefinition?.activities?.activityTypes?.length;
    return (!!hasScene ||
        !!hasConnector ||
        !!hasActivies ||
        hasMeetingExtension(appDefinition) ||
        isApiBasedMessageExtension(appDefinition));
}
function getFeaturesFromAppDefinition(appDefinition) {
    const features = [];
    const personalTab = "personal-tab";
    const groupTab = "group-tab";
    const bot = "bot";
    const messageExtension = "messaging-extension";
    if (isPersonalApp(appDefinition)) {
        features.push(personalTab);
    }
    if (isGroupApp(appDefinition)) {
        features.push(groupTab);
    }
    if (isBot(appDefinition)) {
        features.push(bot);
    }
    if (containsBotBasedMessageExtension(appDefinition)) {
        features.push(messageExtension);
    }
    return features;
}
function hasMeetingExtension(appDefinition) {
    return (!!appDefinition.configurableTabs &&
        appDefinition.configurableTabs.length > 0 &&
        meetingExtensionConfigured(appDefinition.configurableTabs[0]));
}
const groupAppConfigured = (tab) => {
    const validGroupAppContext = (0, lodash_1.includes)(tab.context, MeetingsContext.ChannelTab) ||
        (0, lodash_1.includes)(tab.context, MeetingsContext.PrivateChatTab);
    const validGroupAppScope = (!!tab.scopes && includeGroupChatScope(tab.scopes)) || includeTeamScope(tab.scopes);
    return validGroupAppScope && validGroupAppContext;
};
const meetingExtensionConfigured = (tab) => {
    const validMeetingContext = (0, lodash_1.includes)(tab.context, MeetingsContext.SidePanel) ||
        (0, lodash_1.includes)(tab.context, MeetingsContext.DetailsTab) ||
        (0, lodash_1.includes)(tab.context, MeetingsContext.ChatTab);
    const validMeetingScope = !!tab.scopes && includeGroupChatScope(tab.scopes);
    return validMeetingScope && validMeetingContext;
};
const includeTeamScope = (scopes) => {
    return !!scopes.find((scope) => scope.toLowerCase() === CommandScope.Team.toLowerCase());
};
const includeGroupChatScope = (scopes) => {
    return !!scopes.find((scope) => scope.toLowerCase() === CommandScope.GroupChat.toLowerCase());
};
var CommandScope;
(function (CommandScope) {
    CommandScope["Team"] = "team";
    CommandScope["Personal"] = "personal";
    CommandScope["GroupChat"] = "groupChat";
})(CommandScope || (exports.CommandScope = CommandScope = {}));
var MeetingsContext;
(function (MeetingsContext) {
    MeetingsContext["ChannelTab"] = "channelTab";
    MeetingsContext["PrivateChatTab"] = "privateChatTab";
    MeetingsContext["SidePanel"] = "meetingSidePanel";
    MeetingsContext["ShareToStage"] = "meetingStage";
    MeetingsContext["DetailsTab"] = "meetingDetailsTab";
    MeetingsContext["ChatTab"] = "meetingChatTab";
})(MeetingsContext || (exports.MeetingsContext = MeetingsContext = {}));
function normalizePath(path, useForwardSlash) {
    return useForwardSlash ? path.replace(/\\/g, "/") : path;
}
async function getResolvedManifest(content, path, manifestType, ctx) {
    const vars = (0, common_1.getEnvironmentVariables)(content);
    let telemetryKey;
    switch (manifestType) {
        case envFunctionUtils_1.ManifestType.ApiSpec:
            telemetryKey = telemetry_1.TelemetryPropertyKey.customizedOpenAPIKeys;
            break;
        case envFunctionUtils_1.ManifestType.PluginManifest:
            telemetryKey = telemetry_1.TelemetryPropertyKey.customizedAIPluginKeys;
            break;
        case envFunctionUtils_1.ManifestType.DeclarativeCopilotManifest:
            telemetryKey = telemetry_1.TelemetryPropertyKey.customizedCopilotGptKeys;
            break;
        default:
            telemetryKey = telemetry_1.TelemetryPropertyKey.customizedKeys;
            break;
    }
    if (ctx instanceof wrapUtil_1.WrapDriverContext) {
        ctx.addTelemetryProperties({
            [telemetryKey]: vars.join(";"),
        });
    }
    let value = content;
    if (manifestType !== envFunctionUtils_1.ManifestType.ApiSpec) {
        const processedFunctionRes = await (0, envFunctionUtils_1.expandVariableWithFunction)(content, ctx, undefined, true, manifestType, path);
        if (processedFunctionRes.isErr()) {
            return processedFunctionRes;
        }
        value = (0, common_1.expandEnvironmentVariable)(processedFunctionRes.value);
    }
    else {
        value = (0, common_1.expandEnvironmentVariable)(value);
    }
    const notExpandedVars = (0, common_1.getEnvironmentVariables)(value);
    if (notExpandedVars.length > 0) {
        return (0, teamsfx_api_1.err)(new error_1.MissingEnvironmentVariablesError("manifest", notExpandedVars.join(","), path));
    }
    return (0, teamsfx_api_1.ok)(value);
}
//# sourceMappingURL=utils.js.map